import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Video,
  Plus,
  RefreshCw,
  X,
  Play,
  Youtube,
  Image as ImageIcon,
  Radio,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  CheckCircle2,
  Upload,
  Link as LinkIcon
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ITEM_TYPES = [
  { value: 'live', label: 'Live Stream', icon: Radio, color: 'red' },
  { value: 'recorded', label: 'Recorded Video', icon: Youtube, color: 'red' },
  { value: 'photo', label: 'Photo', icon: ImageIcon, color: 'blue' }
];

const WebcastSection = ({ adminUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoMethod, setPhotoMethod] = useState('upload');
  
  const [formData, setFormData] = useState({
    title: '',
    item_type: 'recorded',
    youtube_url: '',
    photo_url: '',
    description: '',
    is_active: true,
    is_live_now: false,
    display_order: 0
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/webcast`, {
        params: filterType ? { item_type: filterType } : {}
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error loading webcast items:', error);
    }
    setLoading(false);
  }, [filterType]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData({
      title: '',
      item_type: 'recorded',
      youtube_url: '',
      photo_url: '',
      description: '',
      is_active: true,
      is_live_now: false,
      display_order: 0
    });
    setEditingItem(null);
    setPhotoMethod('upload');
    setUploading(false);
    setUploadProgress(0);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      alert('File size must be less than 15MB');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API}/upload/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
      if (response.data.success) {
        setFormData(prev => ({ ...prev, photo_url: response.data.url }));
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error.response?.data?.detail || 'Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) return;
    if ((formData.item_type === 'live' || formData.item_type === 'recorded') && !formData.youtube_url) return;
    if (formData.item_type === 'photo' && !formData.photo_url) return;

    try {
      if (editingItem) {
        await axios.put(`${API}/webcast/${editingItem.id}`, formData);
      } else {
        await axios.post(`${API}/webcast`, formData);
      }
      
      resetForm();
      setShowForm(false);
      loadData();
    } catch (error) {
      console.error('Error saving webcast item:', error);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      item_type: item.item_type,
      youtube_url: item.youtube_url || '',
      photo_url: item.photo_url || '',
      description: item.description || '',
      is_active: item.is_active,
      is_live_now: item.is_live_now || false,
      display_order: item.display_order || 0
    });
    setEditingItem(item);
    // Detect if photo was uploaded (starts with /api/ or contains storage URL) vs external URL
    setPhotoMethod(item.photo_url?.startsWith('/api/') || item.photo_url?.includes('storage.') ? 'upload' : 'url');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/webcast/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleToggleLive = async (itemId, isLive) => {
    try {
      await axios.put(`${API}/webcast/${itemId}/toggle-live?is_live=${isLive}`);
      loadData();
    } catch (error) {
      console.error('Error toggling live status:', error);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API}/webcast/${item.id}`, {
        ...item,
        is_active: !item.is_active
      });
      loadData();
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Handle different YouTube URL formats including shorts and live
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
      /youtube\.com\/live\/([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?rel=0`;
      }
    }
    
    return '';
  };

  const getTypeInfo = (type) => {
    return ITEM_TYPES.find(t => t.value === type) || ITEM_TYPES[1];
  };

  // Group items by type
  const liveItems = items.filter(i => i.item_type === 'live');
  const videoItems = items.filter(i => i.item_type === 'recorded');
  const photoItems = items.filter(i => i.item_type === 'photo');

  return (
    <div className="space-y-6" data-testid="webcast-section">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-rose-500">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Webcast Management</h2>
              <p className="text-sm text-slate-500">Manage live streams, videos, and photo gallery</p>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={loadData}
              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </motion.button>
            <motion.button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-medium shadow-lg flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              Add Content
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-6 overflow-x-auto">
          <button
            onClick={() => setFilterType('')}
            className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
              !filterType ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {ITEM_TYPES.map(type => (
            <button
              key={type.value}
              onClick={() => setFilterType(type.value)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                filterType === type.value
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <type.icon className="w-4 h-4" />
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowForm(false);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">
                  {editingItem ? 'Edit Content' : 'Add Content'}
                </h3>
                <button 
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }} 
                  className="p-1 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Content Type</label>
                  <div className="flex gap-2">
                    {ITEM_TYPES.map(type => (
                      <button
                        key={type.value}
                        onClick={() => setFormData(prev => ({ ...prev, item_type: type.value }))}
                        className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                          formData.item_type === type.value
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <type.icon className="w-4 h-4" />
                        {type.label.split(' ')[0]}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                  <input
                    type="text"
                    placeholder="Enter title"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                {(formData.item_type === 'live' || formData.item_type === 'recorded') && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">YouTube URL *</label>
                    <div className="relative">
                      <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                      <input
                        type="url"
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full pl-10 pr-4 border-2 border-slate-200 rounded-xl py-3 focus:border-red-400 outline-none"
                        value={formData.youtube_url}
                        onChange={e => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                      />
                    </div>
                    {formData.youtube_url && getYouTubeEmbedUrl(formData.youtube_url) && (
                      <div className="mt-3 rounded-xl overflow-hidden aspect-video">
                        <iframe
                          src={getYouTubeEmbedUrl(formData.youtube_url)}
                          className="w-full h-full"
                          allowFullScreen
                          title="Preview"
                        />
                      </div>
                    )}
                  </div>
                )}

                {formData.item_type === 'photo' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Photo *</label>
                    {/* Upload Method Tabs */}
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setPhotoMethod('upload')}
                        data-testid="photo-method-upload"
                        className={`flex-1 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                          photoMethod === 'upload' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <Upload className="w-4 h-4" /> Upload Photo
                      </button>
                      <button
                        type="button"
                        onClick={() => setPhotoMethod('url')}
                        data-testid="photo-method-url"
                        className={`flex-1 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                          photoMethod === 'url' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <LinkIcon className="w-4 h-4" /> Paste URL
                      </button>
                    </div>

                    {photoMethod === 'upload' ? (
                      <div>
                        {!formData.photo_url ? (
                          <label className="block cursor-pointer">
                            <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                              uploading ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                            }`}>
                              {uploading ? (
                                <>
                                  <div className="w-10 h-10 mx-auto mb-2 rounded-full border-3 border-blue-500 border-t-transparent animate-spin" />
                                  <p className="text-sm text-blue-600 font-medium">Uploading... {uploadProgress}%</p>
                                  <div className="w-full bg-blue-100 rounded-full h-2 mt-2">
                                    <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                                  <p className="text-sm text-slate-600">Click to upload photo</p>
                                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, GIF, WebP - Max 15MB</p>
                                </>
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/gif,image/webp"
                              className="hidden"
                              onChange={handlePhotoUpload}
                              disabled={uploading}
                              data-testid="photo-file-input"
                            />
                          </label>
                        ) : (
                          <div className="relative rounded-xl overflow-hidden">
                            <img src={formData.photo_url.startsWith('/api/') ? `${API.replace('/api', '')}${formData.photo_url}` : formData.photo_url}
                              alt="Preview" className="w-full h-48 object-cover"
                              onError={e => { e.target.style.display = 'none'; }} />
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, photo_url: '' }))}
                              className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="relative">
                          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                          <input type="url" placeholder="https://example.com/photo.jpg"
                            className="w-full pl-10 pr-4 border-2 border-slate-200 rounded-xl py-3 focus:border-blue-400 outline-none"
                            value={formData.photo_url}
                            onChange={e => setFormData(prev => ({ ...prev, photo_url: e.target.value }))}
                            data-testid="photo-url-input"
                          />
                        </div>
                        {formData.photo_url && (
                          <div className="mt-3 rounded-xl overflow-hidden">
                            <img src={formData.photo_url} alt="Preview" className="w-full h-48 object-cover"
                              onError={e => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    placeholder="Enter description (optional)"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none"
                    rows={3}
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-sm text-slate-700">Active (visible on frontend)</span>
                  </label>
                </div>

                {formData.item_type === 'live' && (
                  <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl bg-red-50 border border-red-200">
                    <input
                      type="checkbox"
                      checked={formData.is_live_now}
                      onChange={e => setFormData(prev => ({ ...prev, is_live_now: e.target.checked }))}
                      className="w-5 h-5 rounded border-red-300 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-sm text-red-700 font-medium">Currently Live</span>
                    <Radio className="w-4 h-4 text-red-500 animate-pulse ml-auto" />
                  </label>
                )}

                <div className="flex gap-3 pt-4">
                  <motion.button
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleSubmit}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    {editingItem ? 'Update' : 'Create'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin text-red-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
          <Video className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 font-medium">No webcast content yet</p>
          <p className="text-sm text-slate-500 mt-1">Add live streams, videos, or photos to your webcast page</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Live Streams Section */}
          {(!filterType || filterType === 'live') && liveItems.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Radio className="w-5 h-5 text-red-500" />
                Live Streams ({liveItems.length})
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                {liveItems.map(item => (
                  <WebcastCard
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleLive={handleToggleLive}
                    onToggleActive={handleToggleActive}
                    getYouTubeEmbedUrl={getYouTubeEmbedUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {(!filterType || filterType === 'recorded') && videoItems.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-500" />
                Recorded Videos ({videoItems.length})
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                {videoItems.map(item => (
                  <WebcastCard
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleLive={handleToggleLive}
                    onToggleActive={handleToggleActive}
                    getYouTubeEmbedUrl={getYouTubeEmbedUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Photos Section */}
          {(!filterType || filterType === 'photo') && photoItems.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-500" />
                Photo Gallery ({photoItems.length})
              </h3>
              <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {photoItems.map(item => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden group relative"
                  >
                    <div className="aspect-square">
                      <img
                        src={item.photo_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white font-medium text-sm truncate">{item.title}</p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(item)}
                            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white"
                          >
                            {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-1.5 rounded-lg bg-red-500/50 hover:bg-red-500/70 text-white"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {!item.is_active && (
                      <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-slate-900/70 text-white text-xs">
                        Hidden
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Webcast Card Component for Videos and Live Streams
const WebcastCard = ({ item, onEdit, onDelete, onToggleLive, onToggleActive, getYouTubeEmbedUrl }) => {
  const embedUrl = getYouTubeEmbedUrl(item.youtube_url);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden"
    >
      {/* Video Preview */}
      {embedUrl && (
        <div className="aspect-video relative">
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allowFullScreen
            title={item.title}
          />
          {item.is_live_now && (
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-red-500 text-white text-sm font-medium flex items-center gap-2 animate-pulse">
              <Radio className="w-4 h-4" />
              LIVE
            </div>
          )}
          {!item.is_active && (
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-slate-900/70 text-white text-sm">
              Hidden
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        <h4 className="font-bold text-slate-800 mb-1">{item.title}</h4>
        {item.description && (
          <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {item.item_type === 'live' && (
            <motion.button
              onClick={() => onToggleLive(item.id, !item.is_live_now)}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${
                item.is_live_now
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Radio className="w-4 h-4" />
              {item.is_live_now ? 'End Live' : 'Go Live'}
            </motion.button>
          )}
          <motion.button
            onClick={() => onToggleActive(item)}
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${
              item.is_active
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {item.is_active ? 'Hide' : 'Show'}
          </motion.button>
          <motion.button
            onClick={() => onEdit(item)}
            className="px-3 py-2 rounded-lg bg-blue-100 text-blue-700 text-sm font-medium hover:bg-blue-200 flex items-center gap-1"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit className="w-4 h-4" />
            Edit
          </motion.button>
          <motion.button
            onClick={() => onDelete(item.id)}
            className="px-3 py-2 rounded-lg bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 flex items-center gap-1"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default WebcastSection;
